import { Button } from "@gigpro/ui/Button";
import { DialogFooter } from "@gigpro/ui/Dialog";
import type { JobsSubscriptionStatus } from "@sidegigllc/api/client";
import { AddPaymentMethodNote } from "domains/hire/components/AddPaymentMethodNote";
import { JOBS_PLAN_AMOUNT, JOBS_PLAN_PRICE, jobsNeedsPaymentMethod, jobsPaymentLabel } from "domains/hire/jobsPlan";

type JobsResubscribeStepProps = {
  locationId: number;
  locationName: string;
  status: JobsSubscriptionStatus;
  /** Only admins and Qwick staff resubscribe; anyone else is asked to get an admin. */
  isAdmin: boolean;
  /** Making working interviews, unlocking applicants from the teaser, or starting from the plan card. */
  purpose?: "workingInterviews" | "unlock" | "settings";
  /** The plan is already charged and active; the button only retries what follows. */
  resubscribed?: boolean;
  isStarting: boolean;
  onStart: () => void;
  onBack: () => void;
};

/** The trial step's counterpart for a location that already used its trial: resubscribing charges right away. */
export const JobsResubscribeStep = ({
  locationId,
  locationName,
  status,
  isAdmin,
  purpose = "workingInterviews",
  resubscribed = false,
  isStarting,
  onStart,
  onBack,
}: JobsResubscribeStepProps) => {
  const payment = jobsPaymentLabel(status);
  const needsMethod = jobsNeedsPaymentMethod(status);
  const title =
    purpose === "workingInterviews" ? "Resubscribe to make these working interviews" : "Resubscribe to your Jobs plan";

  if (!isAdmin) {
    return (
      <div className="space-y-4 pt-4">
        <h3 className="text-extraLarge font-semibold">{title}</h3>
        <p>Only an admin at {locationName} can resubscribe. Ask one of them to resubscribe.</p>
        <DialogFooter className="mt-6">
          <Button variant="contained" onClick={onBack}>
            Back
          </Button>
        </DialogFooter>
      </div>
    );
  }

  const paymentLine = () => {
    if (resubscribed) return "Your Jobs plan is active again and you've been charged. You won't be charged twice.";
    if (status.collection === "INVOICE") return `${JOBS_PLAN_AMOUNT} goes on your monthly Qwick invoice from today.`;
    if (!needsMethod) return `We'll charge ${JOBS_PLAN_AMOUNT} to ${payment} now, then every month.`;
    return <AddPaymentMethodNote locationId={locationId} />;
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="space-y-2">
        <h3 className="text-extraLarge font-semibold">{title}</h3>
        <p>See every applicant at {locationName}. There's no free trial this time.</p>
        {!resubscribed && <p className="font-semibold">{JOBS_PLAN_AMOUNT} now, then every month. Cancel anytime.</p>}
      </div>
      <p className="rounded-sm bg-background p-3 text-small">{paymentLine()}</p>
      <DialogFooter className="mt-6 grid grid-cols-2 gap-2">
        <Button variant="text" onClick={onBack} disabled={isStarting}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onStart}
          loading={isStarting}
          disabled={(needsMethod && !resubscribed) || isStarting}
        >
          {resubscribed ? "Try again" : `Resubscribe for ${JOBS_PLAN_PRICE}`}
        </Button>
      </DialogFooter>
    </div>
  );
};
